import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Archive, Loader2 } from 'lucide-react';
import { getApiHeaders } from '../../utils/apiHelpers';
import { API_ARCHIVIO } from './archivioApi';
import { ArchivioIntestazione, ProgressoScansioni } from './ArchivioIntestazione';
import { FornitoreArchivio } from './FornitoreArchivio';
import { ProposteDoppioni } from './ProposteDoppioni';

// Nome fornitore che corrisponde: tutto il gruppo; altrimenti solo i DDT con quel numero
const filtra = (f, q) => {
  if (!q || [f.nome, ...f.varianti_nome].some((n) => n.toLowerCase().includes(q))) return f;
  const ddt = f.ddt.filter((d) => String(d.numero_ddt_fornitore || '').toLowerCase().includes(q));
  return ddt.length ? { ...f, ddt } : null;
};

export const ArchivioDDT = () => {
  const [dati, setDati] = useState(null);
  const [cerca, setCerca] = useState('');
  const [job, setJob] = useState(null);
  const annoRef = useRef(null);

  const carica = useCallback(async (anno = annoRef.current) => {
    try {
      const { data } = await axios.get(API_ARCHIVIO, { headers: getApiHeaders(), params: anno ? { anno } : {} });
      annoRef.current = data.anno;
      setDati(data);
      if (data.da_creare > 0) {
        setJob((j) => ({ in_corso: true, in_coda: data.da_creare, totale: Math.max(j?.totale || 0, data.da_creare) }));
      }
      return data;
    } catch (e) {
      toast.error("Impossibile caricare l'archivio DDT");
      setDati((d) => d || { anni: [], fornitori: [] });
      return null;
    }
  }, []);

  useEffect(() => {
    carica();
  }, [carica]);

  // Le scansioni le fa il server in coda: qui si segue solo l'avanzamento (leggero) e si aggiorna la lista ogni tanto
  useEffect(() => {
    if (!job?.in_corso) return undefined;
    let ultimoAggiornamento = Date.now();
    const id = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API_ARCHIVIO}/genera/stato`, { headers: getApiHeaders() });
        setJob((j) => ({ ...data, totale: Math.max(j?.totale || 0, data.in_coda) }));
        if (!data.in_corso || Date.now() - ultimoAggiornamento > 20000) {
          ultimoAggiornamento = Date.now();
          carica();
        }
      } catch (e) {
        console.error('Stato scansioni non disponibile', e);
      }
    }, 5000);
    return () => clearInterval(id);
  }, [job?.in_corso, carica]);

  const q = cerca.trim().toLowerCase();
  const fornitori = useMemo(() => (dati?.fornitori || []).map((f) => filtra(f, q)).filter(Boolean), [dati, q]);

  if (!dati) {
    return (
      <div className="flex justify-center py-12" data-testid="archivio-caricamento">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="archivio-ddt">
      <ArchivioIntestazione dati={dati} cerca={cerca} onCerca={setCerca} onAnno={(a) => carica(a)} />
      <ProgressoScansioni job={job} />
      {!q && <ProposteDoppioni proposte={dati.proposte_doppioni} onFatto={() => carica()} />}
      {fornitori.length === 0 ? (
        <div className="text-center py-12 rounded-2xl border border-slate-700 bg-slate-800/50 lg:bg-white lg:border-gray-100" data-testid="archivio-vuoto">
          <Archive className="h-10 w-10 mx-auto mb-3 text-slate-500 lg:text-gray-300" />
          <p className="text-slate-400 lg:text-gray-500">{q ? 'Nessun DDT corrisponde alla ricerca' : 'Nessun DDT in archivio per questo anno'}</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="archivio-fornitori">
          {fornitori.map((f, i) => (
            <FornitoreArchivio key={`${dati.anno}-${f.chiave}`} fornitore={f} indice={i} forzaAperto={!!q} onAggiorna={() => carica()} />
          ))}
        </div>
      )}
    </div>
  );
};
